import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';

import { EyeIcon, PaymentLinksIcon } from '../../../../assets/svg';
import { TicketIcon } from '../../../../assets/svg/TicketIcon';
import { Breadcrumb } from '../../../../components/breadcrumb';
import { DateAndTime } from '../../../../components/date-and-time';
import { ETPayFilters } from '../../../../components/table/components/filters';
import { ViewWrapper } from '../../../../components/view-wrapper';
import { CONSTANTS } from '../../../../config/constants';
import { useFloatingAlert } from '../../../../hooks/useFloatingAlert';
import { useMerchant } from '../../../../hooks/useMerchant';
import { Payment } from '../../../../models/common-payments';
import { ColumnInterface, Filters } from '../../../../models/table';
import { PaymentLinksCustomServices } from '../../../../services/payment-links-custom';
import { validatePermission } from '../../../../utils/common';
import { defaultPagination } from '../../../../utils/defaults';
import { ExportModal } from '../../components/export-modal';
import { ContainerActionsLinksComponent } from '../../components/links-table/ContainerActionsComponent';
import { ModalPayment } from '../../components/modal-payment';
import { ModalPaymentProps } from '../../components/modal-payment/types';
import { NewLinkPL } from '../../components/new-link';
import { StatusCustomPaymentLink } from '../../components/status';
import { PaymentLinkTable } from '../../components/table';
import { AmountContainer, NoWrapContainer } from '../../components/table/styles';
import { TEXTS } from '../../constants/texts.constants';
import { ContainerActions, SimpleButton, SkeletonSVG } from '../links/styles';
import {
	ButtonsContainer,
	HeadContainer,
	NewPaymentLinkButton,
	PLCardIcon,
	TableFilterHeader,
	TitlePage,
} from './styles';

type PaymentLink = 'single' | 'batch';

interface ModalPaymentLink {
	show: boolean;
	type?: PaymentLink;
}

const defaultModalPaymentLink: ModalPaymentLink = {
	show: false,
	type: undefined,
};

const defaultFilters: Filters = {
	orderBy: 'asc',
	key: '',
	status: undefined,
	dateRange: undefined,
};

const defaultModalPayment: ModalPaymentProps = {
	show: false,
	onClose: () => {
		console.log('close');
	},
	data: {} as Payment,
};

const MAXRETRIES = 3;

const AllPaymentsPage = () => {
	const dictionary = new Map([
		['payment-link-custom', 'Links de pago'],
		['all', 'Pagos recibidos'],
	]);
	const { handleShowAlert } = useFloatingAlert();

	const [modal, setModal] = useState(defaultModalPaymentLink);
	const [modalPayment, setModalPayment] = useState(defaultModalPayment);
	const [modalExport, setModalExport] = useState({
		show: false,
		loading: false,
		url: '',
		countRetry: 0,
		haveError: false,
	});

	const { merchantSelected, getFlowBankorStp } = useMerchant();
	const flowBanksOrStp = getFlowBankorStp();
	const [pagination, setPagination] = useState(defaultPagination);
	const [filters, setFilters] = useState(defaultFilters);
	const [total, setTotal] = useState(0);

	const {
		data: table,
		isFetching,
		isFetched,
		isLoading: isLoadingTable,
		refetch,
	} = useQuery({
		enabled: !!merchantSelected,
		useErrorBoundary: true,
		keepPreviousData: true,
		queryKey: [
			CONSTANTS.services.paymentLinksCustom.getAllPayments,
			merchantSelected?.code,
			pagination,
			filters,
		],
		queryFn: () =>
			PaymentLinksCustomServices.getAllPayments({
				merchantCode: merchantSelected?.code as string,
				pagination,
				filters: {
					...filters,
					stateSelected: filters?.status,
					dateRange: {
						from: filters?.dateRange?.from,
						to: filters?.dateRange?.to,
					},
				},
			}),

		onSuccess: (data) => {
			if (data) setTotal(data.total);
		},
	});

	const handleDownloadVoucher = (rowData: Payment) => {
		if (rowData.urlCEP) window.open(rowData.urlCEP, '_blank');
		else if (rowData.validationMailPdf) {
			window.open(rowData.validationMailPdf, '_blank');
		} else {
			handleShowAlert({
				message: CONSTANTS.alertMessages.noVoucherAvailable,
				type: 'error',
			});
		}
	};

	const getColumns = () => {
		const columns: ColumnInterface<Payment>[] = [
			{
				sortField: true,
				title: 'Fecha de creación',
				propertyName: 'createdatDate',
				align: 'left',
				cellRender: ({ validatedatDate, validatedatTime }) => (
					<DateAndTime date={validatedatDate} time={validatedatTime} />
				),
			},
			{
				sortField: true,
				title: 'Monto',
				align: 'left',
				propertyName: 'amount',
				cellRender: ({ amount }) => <AmountContainer>{amount}</AmountContainer>,
			},
			{
				title: 'RUT Pagador',
				align: 'left',
				propertyName: 'rut',
				cellRender: ({ rut }) => <NoWrapContainer>{rut}</NoWrapContainer>,
			},
			{
				sortField: true,
				title: 'Banco',
				align: 'left',
				propertyName: 'bankName',
				cellRender: ({ bankName }) => (
					<>{bankName === 'DESCONOCIDO' && merchantSelected?.country === 'MX' ? null : bankName}</>
				),
			},
			{
				sortField: true,
				title: 'Correo electrónico',
				align: 'left',
				propertyName: 'email',
				cellRender: ({ email }) => <>{email}</>,
			},
			{
				title: 'Estatus',
				align: 'left',
				propertyName: 'status',
				cellRender: ({ status }) => (
					<AmountContainer>
						<StatusCustomPaymentLink status={status} />
					</AmountContainer>
				),
			},
			{
				sortField: true,
				title: 'Nombre de enlace usado',
				align: 'left',
				propertyName: 'link_payment',
				cellRender: ({ presession }) => <>{presession.name}</>,
			},
			{
				title: '',
				propertyName: '',
				align: 'left',
				cellRender: (rowData: Payment) => (
					<ContainerActionsLinksComponent disabled={false}>
						<ContainerActions>
							<SimpleButton
								onClick={() => handleDownloadVoucher(rowData)}
								disabled={!rowData.urlCEP && !rowData.validationMailPdf}
							>
								{flowBanksOrStp && (rowData.urlCEP || rowData.validationMailPdf) ? (
									<TicketIcon />
								) : (
									<SkeletonSVG />
								)}
							</SimpleButton>
							<SimpleButton onClick={() => handleShowPaymentModal(rowData)}>
								<EyeIcon />
							</SimpleButton>
						</ContainerActions>
					</ContainerActionsLinksComponent>
				),
			},
		];

		return columns;
	};

	const handleShowModal = (type: PaymentLink) => {
		setModal({ show: true, type });
	};

	const handleCloseModal = () => {
		setModal(defaultModalPaymentLink);
		refetch();
	};

	const handleShowPaymentModal = (payment: Payment) => {
		setModalPayment({ ...modalPayment, show: true, data: payment });
	};

	const handleClosePaymentModal = () => {
		setModalPayment(defaultModalPayment);
	};

	const handleCloseExportModal = () => {
		setModalExport({
			...modalExport,
			show: false,
			loading: false,
		});
	};

	const handleExportAction = async () => {
		setModalExport({
			...modalExport,
			loading: true,
			show: true,
		});

		try {
			const response = await PaymentLinksCustomServices.getExportPaymentData({
				merchantCode: merchantSelected?.code as string,
				dateRange: {
					from: filters?.dateRange?.from,
					to: filters?.dateRange?.to,
				},
				search: filters?.search,
				stateSelected: filters?.status,
			});
			if (response.url) {
				setModalExport({
					...modalExport,
					show: true,
					loading: false,
					haveError: false,
					countRetry: 0,
					url: response.url as string,
				});
			}
		} catch (e: any) {
			handleShowAlert({
				type: 'error',
				message: 'Hubo un error al exportar los datos, intenta nuevamente',
			});
			setModalExport({
				...modalExport,
				loading: false,
				show: true,
				haveError: true,
				countRetry: modalExport.countRetry + 1,
				url: '',
			});
		}
	};

	useEffect(() => {
		setFilters(defaultFilters);
		setPagination(defaultPagination);
	}, [merchantSelected]);

	return (
		<ViewWrapper title="Payment Links" isLoading={isLoadingTable || isFetching}>
			{modal.show && modal.type === 'single' && validatePermission('PL_NEW') && (
				<NewLinkPL show={modal.show} onClose={handleCloseModal} />
			)}

			{modalPayment.show && (
				<ModalPayment
					show={modalPayment.show}
					onClose={handleClosePaymentModal}
					data={modalPayment.data}
				/>
			)}

			{modalExport.show && (
				<ExportModal
					title="Descargar pagos"
					isOpen={modalExport.show}
					isLoading={modalExport.loading}
					retryError={modalExport.haveError && modalExport.countRetry < MAXRETRIES}
					closeError={modalExport.haveError && modalExport.countRetry >= MAXRETRIES}
					onClose={handleCloseExportModal}
					linkExport={modalExport.url}
					retryFunction={handleExportAction}
				/>
			)}
			<HeadContainer>
				<PLCardIcon selected>
					<TitlePage>
						<PaymentLinksIcon />
					</TitlePage>
				</PLCardIcon>
				<Breadcrumb dictionary={dictionary} />

				<ButtonsContainer>
					<NewPaymentLinkButton onClick={() => handleShowModal('single')}>
						{TEXTS.CTA}
					</NewPaymentLinkButton>
				</ButtonsContainer>
			</HeadContainer>
			<TableFilterHeader>
				<ETPayFilters
					filters={filters}
					onFilterChange={setFilters}
					pagination={pagination}
					setPagination={setPagination}
				/>
			</TableFilterHeader>

			<PaymentLinkTable
				titleTable=" "
				allowExport={true}
				handleExportAction={handleExportAction}
				data={table?.tableInfo || []}
				columns={getColumns()}
				filters={filters}
				hasFilters={true}
				hasSearch={true}
				isLoading={isFetching && !isFetched}
				onFilterChange={setFilters}
				pagination={pagination}
				setPagination={setPagination}
				stateList={table?.typeOfStatus || []}
				totalResults={total}
			/>
		</ViewWrapper>
	);
};

export { AllPaymentsPage };
